import { Box, Divider, Flex, Icon, Stack, Text } from '@chakra-ui/react';
import { memo, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Link as ReactRouterLink } from 'react-router-dom';

import { useUserStore } from '@store/app.store';
import { useBreakpoints, useBreakpointValue } from '@util/hooks';
import { formatTimeDistance, getInsightLabels, getLensIconComponent } from '@util/helpers';
import ReskinCardHeader from '@components/general/card/reskin-card-structure/reskin-card-header.component';
import CardActions from '@components/general/card/reskin-summary-card/reskin-summary-card-actions.component';
import LabelsBarReadOnly from '@components/general/labels/labels-bar-read-only.component';

/**
 * @typedef {Object} InsightCardProps
 * @property {Insight} insight
 * @property {(action: string, value: any, insight: Insight) => void} onInsightActionClick
 *
 * @param {InsightCardProps} props
 */
const InsightCard = ({ insight, onInsightActionClick }) => {
  const user = useUserStore((state) => state.user);
  const [isHovering, setIsHovering] = useState(false);
  const { isMobile } = useBreakpoints();
  const timeDistanceVariant = useBreakpointValue({ base: 'single', md: 'long' });
  const headerSizeVariant = useBreakpointValue({ base: 'md', md: 'lg' });
  const titleLink = `/insights/${insight?.id}`;
  const labels = useMemo(() => getInsightLabels(insight), [insight]);

  return (
    <Stack
      layerStyle='section-card'
      borderRadius='xl'
      data-testid='reskin-summary-card'
      gap={0}
      onMouseOver={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <ReskinCardHeader
        title={insight?.headline}
        titleLink={titleLink}
        size={headerSizeVariant}
        leftElement={
          <Icon
            as={getLensIconComponent(insight?.initiative)}
            color={insight?.initiative?.color || 'brandPrimary.onContainer'}
            boxSize={6}
            my={1.5}
            mr={2}
          />
        }
      >
        <Flex
          flexDir={{ base: 'column', md: 'row' }}
          gap={{ base: '7px', md: 'unset' }}
        >
          {insight?.updated_at && (
            <Text
              variant='label-reg-lg'
              color='surface.onSurfaceVariant'
            >
              {format(insight?.updated_at, 'ccc, LLL d, yyyy h:mm a')}
              <Text
                as='span'
                ml={3}
              >
                {formatTimeDistance(insight?.updated_at, timeDistanceVariant)}
              </Text>
            </Text>
          )}
          <Flex ml={{ base: 0, md: 'auto' }}>
            <Text
              as='span'
              color='surface.onSurfaceVariant'
              variant='label-lighter-md'
              lineHeight='18px'
              mr={1.5}
            >
              In
            </Text>
            <Text
              as='span'
              color={insight?.initiative?.color}
              variant='label-caps-600-md'
              letterSpacing='1.5px'
              layerStyle='ellipsis-text'
            >
              {insight?.initiative?.title}
            </Text>
          </Flex>
        </Flex>
      </ReskinCardHeader>
      <Divider style={{ margin: 0 }} />
      <Box
        as={ReactRouterLink}
        to={titleLink}
        px={{ base: 4, md: 8 }}
        pt={3}
        pb={1.5}
      >
        <Text
          variant='body-lighter-md'
          as='span'
        >
          {insight?.subheadline}
        </Text>
      </Box>

      <Flex
        justify='space-between'
        px={{ base: 3, md: 6 }}
        pb={{ base: 2, md: 0 }}
      >
        <LabelsBarReadOnly
          labels={labels}
          pb={{ base: 0, md: 2 }}
        >
          {!user.isGuest && (
            <CardActions
              enableCollections={false}
              card={insight}
              onCardActionClick={onInsightActionClick}
              isMobile={isMobile}
              isHovering={isHovering}
              showBody
            />
          )}
        </LabelsBarReadOnly>
      </Flex>
    </Stack>
  );
};

export default memo(InsightCard);
